<script lang="ts">
	import Carousel from '$lib/carousel.svelte';
	import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
	import ArrowLeft from '$lib/icons/arrowLeft.svelte';
	import ArrowRight from '$lib/icons/arrowRight.svelte';
	import { carousels } from '$lib/state/storyblok/carousel.svelte';
	import { fly } from 'svelte/transition';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';

	interface Props {
		content: ISbStoryData<{
			id: string;
			items: ISbStoryData[];
			loop: boolean;
			alignment: EmblaOptionsType['align'];
			start_index: number;
			mobile_width: number;
		}>['content'];
	}

	let { content }: Props = $props();

	let embla = $state<EmblaCarouselType>();
	let active = $state(content.start_index || 0);

	let snapCount = $state<number>(content.items.length);

	$effect(() => {
		snapCount = embla?.scrollSnapList().length || content.items.length;
	});

	$effect(() => {
		if (embla && content.id) {
			carousels[content.id] = embla;
		}

		return () => {
			if (content.id) {
				delete carousels[content.id];
			}
		};
	});

	function next() {
		if (embla) {
			embla.scrollNext();
		}
	}

	function previous() {
		if (embla) {
			embla.scrollPrev();
		}
	}

	function getCarouselAlignment(alignment: EmblaOptionsType['align']) {
		if (alignment === 'start') {
			return 'lg:justify-start';
		}

		if (alignment === 'end') {
			return 'lg:justify-end';
		}

		return 'lg:justify-center';
	}

	let carouselClass = $derived(getCarouselAlignment(content.alignment));
</script>

<div use:editable={content} class="group relative my-6 w-full overflow-hidden px-6">
	<Carousel
		id={content.id}
		bind:embla
		bind:currentIndex={active}
		loop={content.loop}
		overflow
		class={['flex gap-x-6', carouselClass]}
		containerClass="rounded-[20px]"
		align={content.alignment || 'center'}
	>
		{#each content.items as item}
			<div
				class="item-width aspect-3/4 flex-none"
				style="--mobile-width: {content.mobile_width || 66}%"
			>
				<Blok content={item} />
			</div>
		{/each}
	</Carousel>

	{#if snapCount > 1}
		{#if active > 0}
			<button
				onclick={previous}
				class="absolute left-10 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full bg-stone-5 p-4 opacity-0 shadow-[0_0_8px_0_rgba(0,0,0,0.12)] transition-opacity duration-500 disabled:opacity-25 max-lg:hidden lg:group-hover:opacity-100"
				disabled={active === 0 && !content.loop}
				transition:fly={{ x: '-100%' }}
			>
				<ArrowLeft class="h-6 w-6" />
			</button>
		{/if}
		{#if active < snapCount - 1}
			<button
				onclick={next}
				class="absolute right-10 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full bg-stone-5 p-4 opacity-0 shadow-[0_0_8px_0_rgba(0,0,0,0.12)] transition-opacity duration-500 disabled:opacity-25 max-lg:hidden lg:group-hover:opacity-100"
				disabled={active >= content.items.length - 1 && !content.loop}
				transition:fly={{ x: '100%' }}
			>
				<ArrowRight class="h-6 w-6" />
			</button>
		{/if}
	{/if}
</div>

<style>
	.item-width {
		flex-basis: var(--mobile-width);

		@media (min-width: 768px) {
			flex-basis: 32%;
		}

		@media (min-width: 1024px) {
			flex-basis: 25%;
		}

		@media (min-width: 1536px) {
			flex-basis: 20%;
		}

		@media (min-width: 1920px) {
			flex-basis: 16%;
		}
	}
</style>
